import {
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  BriefcaseBusiness,
  CalendarClock,
  ClipboardList,
  FileText,
  Handshake,
  Layers,
  Mail,
  Network,
  Settings2,
  Shield,
  SlidersHorizontal,
  Ticket,
  TrendingUp,
  Trophy,
  Wallet,
  Workflow,
  type LucideIcon,
} from 'lucide-react'

import { isRouteEnabledForProject, redirectsList } from '@lib/constants'

export const GROUPED_NAV_LS_KEY = 'groupedNav'

export type NavGroupName = 'Finance' | 'Growth' | 'Operations' | 'Insights' | 'Configuration'

export type NavItem = {
  title: string
  href: string
  /** Rendered beside the title in grouped dropdowns. Omitted for always-visible links, which stay text-only. */
  icon?: LucideIcon
  /** Absent for the handful of links that stay ungrouped in the bar. */
  group?: NavGroupName
  /** Pre-grouping label, so opted-out users see no rename. Drop with ClassicNav. */
  legacyTitle?: string
  /** Absent for routes the classic nav never listed. Drop with ClassicNav. */
  legacyTier?: 'primary' | 'secondary'
}

// Declaration order IS the classic nav's order: primary tier first, then secondary. Do not reorder.
const NAV_ITEMS: NavItem[] = [
  { title: 'Accounts', href: '/accounts', legacyTier: 'primary' },
  { title: 'Users', href: '/users', legacyTier: 'primary' },
  { title: 'Payouts', href: '/payouts', legacyTier: 'primary' },
  { title: 'Referrals', href: '/referrals', legacyTier: 'primary', group: 'Growth', icon: Handshake },
  { title: 'Transactions', href: '/transactions', legacyTier: 'primary', group: 'Finance', icon: ArrowLeftRight },
  { title: 'Deposits', href: '/deposits', legacyTier: 'primary', group: 'Finance', icon: ArrowDownToLine },
  { title: 'Withdrawals', href: '/withdrawals', legacyTier: 'primary', group: 'Finance', icon: ArrowUpFromLine },
  { title: 'Email Templates', href: '/email_templates', legacyTier: 'primary', group: 'Operations', icon: Mail },
  { title: 'Coupons', href: '/coupons', legacyTier: 'primary', group: 'Growth', icon: Ticket },
  { title: 'Tasks', href: '/tasks', legacyTier: 'primary', group: 'Operations', icon: ClipboardList },
  {
    title: 'Scheduled Actions',
    href: '/scheduled_actions',
    legacyTier: 'primary',
    group: 'Operations',
    icon: CalendarClock,
  },
  { title: 'Discount Tiers', href: '/discount_tiers', legacyTier: 'primary', group: 'Growth', icon: Layers },
  { title: 'IB Profiles', href: '/ib_profiles', group: 'Growth', icon: BriefcaseBusiness },
  {
    title: 'Account Templates',
    legacyTitle: 'Templates',
    href: '/templates',
    legacyTier: 'primary',
    group: 'Configuration',
    icon: FileText,
  },
  {
    title: 'Account Groups',
    legacyTitle: 'Groups',
    href: '/groups',
    legacyTier: 'secondary',
    group: 'Configuration',
    icon: Network,
  },
  { title: 'Competitions', href: '/competitions', legacyTier: 'secondary', group: 'Growth', icon: Trophy },
  {
    title: 'Permission Groups',
    href: '/permission_groups',
    legacyTier: 'secondary',
    group: 'Configuration',
    icon: Shield,
  },
  { title: 'App Settings', href: '/app-settings', legacyTier: 'secondary', group: 'Configuration', icon: Settings2 },
]

// Bar order, cold config last — it is also the first group the overflow menu swallows.
const GROUPS: { title: NavGroupName; icon: LucideIcon }[] = [
  { title: 'Finance', icon: Wallet },
  { title: 'Growth', icon: TrendingUp },
  { title: 'Operations', icon: Workflow },
  { title: 'Configuration', icon: SlidersHorizontal },
]

const enabledItems = NAV_ITEMS.filter((item) => isRouteEnabledForProject(item.href))

const toLegacyNav = (tier: NavItem['legacyTier']) =>
  enabledItems
    .filter((item) => item.legacyTier === tier)
    .map(({ title, legacyTitle, href }) => ({ title: legacyTitle ?? title, href }))

export const legacyPrimaryNav = toLegacyNav('primary')
export const legacySecondaryNav = toLegacyNav('secondary')

export type NavSlot =
  { kind: 'link'; item: NavItem } | { kind: 'group'; title: NavGroupName; icon: LucideIcon; items: NavItem[] }

const directSlots: NavSlot[] = enabledItems
  .filter((item) => !item.group)
  .map((item) => ({ kind: 'link' as const, item }))

// Declaration order is pinned to the classic nav, which would put old-CRM links above the in-app page they belong to.
const oldCrmLast = (a: NavItem, b: NavItem) =>
  Number(redirectsList.includes(a.href)) - Number(redirectsList.includes(b.href))

// A group whose routes are mostly disabled for this brand would render as a one-item dropdown — flatten it instead.
const groupSlots: NavSlot[] = GROUPS.map(({ title, icon }) => {
  const items = enabledItems.filter((item) => item.group === title).sort(oldCrmLast)

  return items.length === 1
    ? { kind: 'link' as const, item: items[0]! }
    : { kind: 'group' as const, title, icon, items }
}).filter((slot) => slot.kind === 'link' || slot.items.length > 0)

export const navSlots: NavSlot[] = [...directSlots, ...groupSlots]
